import type { InvoiceComment, InvoiceEvent } from '@/lib/invoice-types'
import { cn } from '@/lib/utils'

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

type FeedItem =
  | { kind: 'event'; at: string; data: InvoiceEvent }
  | { kind: 'comment'; at: string; data: InvoiceComment }

export function InvoiceTimeline({
  events,
  comments,
  className,
}: {
  events: InvoiceEvent[]
  comments: InvoiceComment[]
  className?: string
}) {
  const feed: FeedItem[] = [
    ...events.map((data) => ({ kind: 'event' as const, at: data.createdAt, data })),
    ...comments.map((data) => ({ kind: 'comment' as const, at: data.createdAt, data })),
  ].sort((a, b) => b.at.localeCompare(a.at))

  if (feed.length === 0) {
    return <p className={cn('text-sm text-ink-faint', className)}>Nog geen activiteit.</p>
  }

  return (
    <ol className={cn('relative', className)}>
      <span aria-hidden className="pointer-events-none absolute bottom-2 left-[3.5px] top-2 w-px bg-hairline" />
      {feed.map((item) => {
        const isComment = item.kind === 'comment'
        const summary = isComment ? item.data.body : item.data.summary
        const actor = isComment
          ? item.data.authorName
          : ((item.data as InvoiceEvent).actorName ?? 'systeem')
        const channel = isComment ? 'WEB' : (item.data as InvoiceEvent).channel
        return (
          <li key={`${item.kind}-${item.data.id}`} className="relative flex gap-3 pb-4 last:pb-0">
            <span
              className={cn(
                'relative z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full',
                isComment ? 'bg-accent-600' : 'bg-fill-3 ring-1 ring-hairline',
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ink-soft">{summary}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-ink-faint">
                <span>
                  {actor} · {formatTimestamp(item.at)}
                </span>
                {channel !== 'WEB' ? (
                  <span className="rounded-sm bg-fill-2 px-1 py-px text-[10px] uppercase">{channel}</span>
                ) : null}
              </p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
